import { WitboostTemplate } from '@agilelab/plugin-wb-builder-common';
import { camelToSpacedCase } from '@agilelab/plugin-wb-platform-common';
import pluralize from 'pluralize';

const templateClassTypeOrder: Record<string, number> = {
  systemtype: 1,
  componenttype: 2,
  default: 3,
};

Object.freeze(templateClassTypeOrder);

/**
 * Maps template type/kind strings to user-friendly display names.
 * Use this for displaying template types in the UI (singular form).
 */
export const TEMPLATE_TYPE_DISPLAY_NAMES: Record<string, string> = {
  // Templates
  template: 'Template',
  // Access Control
  accesscontrolrequesttemplate: 'Access Control Request Template',
  // Components
  outputport: 'Output Port',
  dataproduct: 'Data Product',
  workload: 'Workload',
  storage: 'Storage',
  observability: 'Observability',
  // System types
  systemtype: 'System Type',
  componenttype: 'Component Type',
  // Others
  other: 'Other',
};

/**
 * Returns a user-friendly display name for a template type.
 * Falls back to title-cased version if not found in mapping.
 */
export const getTemplateTypeDisplayName = (templateType: string): string => {
  const normalized = templateType.toLowerCase();
  return (
    TEMPLATE_TYPE_DISPLAY_NAMES[normalized] || camelToSpacedCase(templateType)
  );
};

export const templateTitles = (templateType: string) => {
  switch (templateType.toLowerCase()) {
    case 'outputport': {
      return 'Output Ports';
    }
    case 'dataproduct': {
      return 'Data Product';
    }
    case 'workload': {
      return 'Workloads';
    }
    case 'storage': {
      return 'Storages';
    }
    case 'observability': {
      return 'Observability';
    }
    case 'other': {
      return 'Others';
    }
    default: {
      return pluralize(camelToSpacedCase(templateType));
    }
  }
};

export const sortByTemplateClassType = (
  previousEntry: [string, WitboostTemplate[]],
  currentEntry: [string, WitboostTemplate[]],
): number => {
  const getOrder = (entry: [string, WitboostTemplate[]]): number => {
    const type =
      entry[1][0]?.metadata.classDetails?.type?.toLowerCase() ?? 'default';
    return templateClassTypeOrder[type] ?? templateClassTypeOrder.default;
  };

  return getOrder(previousEntry) - getOrder(currentEntry);
};

export const sortTemplateType = (
  previousTemplate: [string, WitboostTemplate[]],
  currentTemplate: [string, WitboostTemplate[]],
): number => {
  return (
    (previousTemplate[1][0].metadata.classDetails?.displayPriority ??
      Number.MAX_VALUE) -
    (currentTemplate[1][0].metadata.classDetails?.displayPriority ??
      Number.MAX_VALUE)
  );
};

export const groupTemplateBySection = (
  entities: WitboostTemplate[],
): Record<string, WitboostTemplate[]> => {
  const grouped = entities.reduce((acc, e) => {
    const type = e.metadata.classDetails
      ? e.metadata.classDetails.pluralizedDisplayName ??
        (e.metadata.classDetails.displayName
          ? pluralize(e.metadata.classDetails.displayName)
          : undefined) ??
        templateTitles('other')
      : templateTitles(e.spec.type);

    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(e);
    return acc;
  }, {} as Record<string, WitboostTemplate[]>);
  return Object.fromEntries(Object.entries(grouped).sort(sortTemplateType));
};
